import { DateTime, DateTimeUnit, DurationLike, DurationUnits } from 'luxon';

export const getDateTimeNow = (): DateTime => DateTime.now();

export const createDate = (year: number, month: number, day: number): DateTime => DateTime.local(year, month, day);

export const getWeekDay = (date: Date | DateTime) => {
  if (!date) {
    return '';
  }
  if (date instanceof DateTime) {
    return date.setLocale('en-US').weekdayLong.toLowerCase();
  }
  return DateTime.fromJSDate(date).setLocale('en-US').weekdayLong.toLowerCase();
};

export const getDateTimeFromISO = (date: string) => {
  return DateTime.fromISO(date);
};

export const formatDate = (date: Date, format: string = 'dd/MM/yyyy') => DateTime.fromJSDate(date).toFormat(format);

export const formatToISO = (date: DateTime, dateOnly = false) => (dateOnly ? date.toISODate() : date.toISO());

export const formatDateFromISO = (date: string, format: string = 'dd/MM/yyyy') =>
  date ? DateTime.fromISO(date).toFormat(format) : null;

export const dateTimeToFormat = (date: DateTime, format: string = 'dd/MM/yyyy') => date.toFormat(format);

export const getStartDateOfWeekForOffset = (offset: number) => {
  const startOfCurrentWeek = getDateTimeNow().startOf('week');
  return startOfCurrentWeek.plus({ week: offset }).toJSDate();
};

export const getEndDateOfWeekForOffset = (offset: number) => {
  const endOfCurrentWeek = getDateTimeNow().endOf('week');
  return endOfCurrentWeek.plus({ week: offset }).toJSDate();
};

export const getWeekNumberForOffset = (offset: number, offsetType = 'week') =>
  getDateTimeNow().plus({ [offsetType]: offset }).weekNumber;

export const getYearForWeekOffset = (offset: number, offsetType = 'week') =>
  getDateTimeNow().plus({ [offsetType]: offset }).year;

export const getMonthForOffset = (offset: number, offsetType = 'week') =>
  getDateTimeNow().plus({ [offsetType]: offset }).month;

export const getStartOf = (date: DateTime, unit: DateTimeUnit) => date.startOf(unit);
export const getDiff = (dateOne: DateTime, dateTwo: DateTime, unit: DateTimeUnit) => dateOne.diff(dateTwo, unit);

export const getMonthNameForOffset = (offset: number, offsetType = 'week') =>
  getMonthName(getDateTimeNow().plus({ [offsetType]: offset }));

export const formatDateString = (isoDateTimeString: string) => {
  // https://moment.github.io/luxon/#/zones?id=setzone
  return DateTime.fromISO(isoDateTimeString, { setZone: true }).toISODate();
};

export const toIsoDate = (date: DateTime): string => date.toISO();

export const getDayInWeek = (date: DateTime) => `common.${getWeekDay(date)}`;

export const getDayMonth = (date: DateTime) => date?.toFormat('dd/MM') ?? '';

export const getMonthName = (date: DateTime) => date?.toFormat('MMMM') ?? '';

export const setTimeZone = (date: DateTime, zone = 'Europe/Brussels') => date.setZone(zone);

export const formatAsBrusselsTime = (date: DateTime) => dateTimeToFormat(setTimeZone(date), 'HH:mm');

export const formatAsBrusselsDateTime = (date: DateTime) => dateTimeToFormat(setTimeZone(date), 'dd/MM HH:mm');

export const getDuration = (firstDate: string, secondDate: string, durationUnits?: DurationUnits) =>
  DateTime.fromISO(firstDate).diff(DateTime.fromISO(secondDate), durationUnits);

export const addTime = (date: DateTime, timeToAdd: DurationLike): DateTime => date.plus(timeToAdd);

export const addWeek = (date: DateTime, count: number): DateTime => addTime(date, { week: count });

export const generateDateFromYearAndWeekNumber = (weekData: { year: number; weekNumber: number }): DateTime =>
  DateTime.fromObject({ weekYear: weekData.year, weekNumber: weekData.weekNumber }).startOf('week');

export const isSameDate = (first: string, second: string) => first?.split('T')?.[0] === second?.split('T')?.[0];

export const convertHHMMToMinutes = (time: string): number => {
  if (!time) return 0;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

export const convertMinutesToHHMM = (minutes: number): string => {
  let hours = Math.floor(minutes / 60);
  let remainingMinutes = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`;
}

export const formatDecimalHoursToHHMM = (decimalHours: number): string => {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export const convertDecimalHoursToMinutes = (decimal: number): number => {
  const hours = Math.floor(decimal);
  const minutes = Math.round((decimal - hours) * 60);
  return hours * 60 + minutes;
};

export const formatLocaleStringAsUTCString = (dateString: string): string =>
  DateTime.fromISO(dateString).toUTC().toString();

export const formatTime = (dateTime: string): string => {
  const date = new Date(dateTime);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const formatToHoursAndMinutes = (time: string): string => {
  if (!time) {
    return null;
  }

  const [hours, minutes] = time.split(':');
  return `${hours}:${minutes}`;
};

export const getWeekSpan = (year: number, weekNumber: number): { firstDay: string; lastDay: string } => {
  const dt = DateTime.fromObject({ weekYear: year, weekNumber });
  return {
    firstDay: dt.startOf('week').toFormat('yyyy-MM-dd'),
    lastDay: dt.endOf('week').toFormat('yyyy-MM-dd'),
  };
};
